import {getCurrentUser, signInWithRedirect} from 'aws-amplify/auth'
import {createRouter, createWebHistory} from 'vue-router'
import {SUCCESS_OAUTH} from 'consts/event'
import $integrations from 'stores/integrations'
import $global from 'stores/global'

import Login from 'pages/login'
import Unsupported from 'pages/unsupported'
import Logout from 'pages/logout'
import Onboarding from 'pages/onboarding'
import OAuth from 'pages/oauth/success-login'
import OAuthError from 'pages/oauth/error'
import RootLayout from 'pages/root-layout'
import Chats from 'pages/chats'
import Files from 'pages/files'
import ShFiles from 'pages/sh-files'
import ShCompanies from 'pages/sh-companies'
import ShTopics from 'pages/sh-topics'
import ShContacts from 'pages/sh-contacts'
import {Tenants, Tenant} from 'pages/tenants'
import Settings from 'pages/settings'
import Primevue from 'pages/primevue.vue'
import ShareChat from 'pages/share-chat'
import Playground from 'pages/playground'
import SharePrompt from 'pages/share-prompt'
import Pulse from 'pages/pulse'
import NewChat from 'pages/new-chat'
import User from 'pages/tenants/user'
import AnalyticsTenant from 'pages/tenant/tenant'

const routes = [
  {
    path: '/login',
    component: Login,
    name: 'login',
    meta: {
      requiresAuth: false,
      allowMobile: true
    },
  },
  {
    path: '/unsupported',
    component: Unsupported,
    name: 'unsupported',
    meta: {
      requiresAuth: false
    },
  },
  {
    path: '/logout',
    name: 'logout',
    meta: {
      requiresAuth: true,
    },
    component: Logout,
  },
  {
    path: '/onboarding',
    name: 'onboarding',
    component: Onboarding,
    meta: {
      requiresAuth: true,
    },
    beforeEnter: (to, from, next) => {
      if (!$global.onboarding_enabled) {
        return next({name: 'new-chat'})
      }
      next()
    }
  },
  {
    path: '/oauth',
    name: 'oauth',
    meta: {
      // small windows can be used for oauth login flows, so allowMobile is true here to show success/error pages properly
      allowMobile: true,
      requiresAuth: false,
    },
    children: [
      {
        path: 'success_login',
        name: 'success_login',
        component: OAuth,
        props: route => ({type: route.query.i_type})
      },
      {
        path: 'error',
        name: 'error',
        component: OAuthError,
        props: route => ({type: route.query.i_type})
      },
      {
        path: 'cancelled_login',
        name: 'cancelled_login'
      },
    ]
  },

  {
    path: '/',
    name: 'root',
    component: RootLayout,
    meta: {
      requiresAuth: true,
    },
    redirect: {
      name: 'pulse'
    },
    children: [
      {
        path: 'new-chat',
        component: NewChat,
        name: 'new-chat',
        meta: {
          allowMobile: true
        }
      },
      {
        name: 'chat',
        path: 'chat',
        component: Chats,
        beforeEnter: (to, _, next) => {
          if (!to.query.id) {
            return next({name: 'new-chat'})
          }
          next()
        },
        meta: {
          allowMobile: true
        }
      },
      {
        path: 'share-chat',
        component: ShareChat,
        name: 'share-chat',
        beforeEnter: (to, _, next) => {
          if (!to.query.id) {
            return next({name: 'new-chat'})
          }
          next()
        },
        meta: {
          allowMobile: true
        }
      },
      {
        path: 'share-prompt',
        component: SharePrompt,
        name: 'share-prompt',
        beforeEnter: (to, _, next) => {
          if (!to.query.id) {
            return next({name: 'new-chat'})
          }
          next()
        }
      },
      {
        path: '/files',
        component: Files,
        meta: {
          requiresAuth: true
        }
      },
      {
        path: '/sh-files',
        component: ShFiles,
        meta: {
          requiresAuth: true
        }
      },
      {
        path: '/sh-companies',
        component: ShCompanies,
        meta: {
          requiresAuth: true
        }
      },
      {
        path: '/sh-topics',
        component: ShTopics,
        meta: {
          requiresAuth: true
        }
      },
      {
        path: '/sh-contacts',
        component: ShContacts,
        meta: {
          requiresAuth: true
        }
      },
      {
        name: 'analytics-tenant',
        path: '/tenant',
        component: AnalyticsTenant,
        meta: {
          requiresAuth: true
        }
      },
      {
        name: 'tenants',
        path: '/tenants',
        component: Tenants,
        meta: {
          requiresAuth: true
        }
      },
      {
        name: 'tenant',
        path: '/tenants/:id',
        component: Tenant,
        meta: {
          requiresAuth: true
        }
      },
      {
        name: 'tenant_user',
        path: '/tenants/:id/users/:user_id',
        component: User,
        meta: {
          requiresAuth: true
        }
      },
      {
        name: 'settings',
        path: '/settings',
        component: Settings,
        meta: {
          requiresAuth: true
        }
      },
      {
        name: 'playground',
        path: '/playground',
        component: Playground,
        meta: {
          requiresAuth: true
        },
      },
      {
        name: 'pulse',
        path: '/pulse',
        component: Pulse,
        meta: {
          requiresAuth: true,
          allowMobile: true
        },
      },
      {
        name: 'primevue',
        path: '/primevue',
        component: Primevue,
        meta: {
          requiresAuth: true
        }
      }
    ]
  },
  {
    path: '/:pathMatch(.*)',
    redirect: {
      name: 'root'
    }
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

router.beforeEach((to, from, next) => {
  const is_handheld = !router.$responsive.desktop
  const is_safari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)

  if (is_handheld && !to.meta.allowMobile) {
    return next({name: 'new-chat'})
  }
  if (is_safari && !is_handheld && to.name !== 'unsupported') {
    return next({name: 'unsupported'})
  }

  if (to.path === '/oauth/success_login') {
    if (!window.opener) {
      return next(to.query?.redirect || '/')
    }
    window.opener.dispatchEvent(new CustomEvent(SUCCESS_OAUTH, {detail: {i_type: to.query.i_type}}))
    return next() 
  }
  else if (to.path === '/oauth/error') {
    if (!window.opener) {
      return next(to.query?.redirect || '/')
    }
    return next()
  }
  else if (to.path === '/oauth/cancelled_login') {
    window.close()
  }
  else if (to.path === '/insufficient_permissions_login') {
    window.close()
  }
  else if (to.path === '/login') {
    if (Object.keys(to.query).length === 0) {
      // clear all redirects if user comes directly to login
      localStorage.removeItem('redirect')
    }
    else if (to.query.redirect) {
      // if there is a redirect save it to local storage
      // othwerise it can be removed during ActiveDirectory login

      // TODO: Implement IDP redirection handling properly
      // const redirectRaw = to.query.redirect
      // if (redirectRaw) {
      //   const u = new URL(redirectRaw, window.location.origin)
      //   const path = u.pathname
      //   const query = Object.fromEntries(u.searchParams.entries())
      //   localStorage.setItem('redirect', JSON.stringify({path, query}))
      // }
      const path = to.query.redirect
      const query = structuredClone(to.query)
      delete query.redirect
      localStorage.setItem('redirect', JSON.stringify({path, query}))
    } else if (to.fullPath === '/login?google_signup') {
      getCurrentUser()
        .then(() => next())
        .catch(() => signInWithRedirect({provider: {custom: 'Google'}}))
    }
    return next()
  }
  else if (to.matched.some(record => record.meta.requiresAuth)) {
    getCurrentUser()
      .then(() => next())
      .catch(() => next({
        name: 'login',
        query: {redirect: to.path, ...to.query}
      }))     
  }
  else {
    return next()
  }
})

if (!window.opener) {
  window.addEventListener(SUCCESS_OAUTH, async (event) => {
    const integration = $integrations.getIntegration(event.detail.i_type)
    await integration.load()
  })
}


export default router